import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
  notify,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { TwoFactorSetup } from "services/authService";
import { getThemeColor } from "theme/colors";

interface TwoFactorSetupWizardProps {
  /** Fetch a fresh secret + otpauth URI (start enrollment). */
  onStart: () => Promise<TwoFactorSetup>;
  /** Verify the first code and enable 2FA. Should throw on an invalid code. */
  onVerify: (code: string) => Promise<void>;
  /** Called after 2FA is successfully enabled. */
  onComplete: () => void;
  /** Optional cancel (omit for forced enrollment, where there is no way out). */
  onCancel?: () => void;
}

const formatSecret = (secret: string): string =>
  secret.replace(/(.{4})/g, "$1 ").trim();

/**
 * Two-step authenticator enrollment: scan the QR (or reveal the secret for manual entry),
 * then confirm with a 6-digit code. Endpoint wiring is injected so the same wizard serves
 * forced login/invite enrollment and voluntary enrollment from the profile.
 */
export default function TwoFactorSetupWizard({
  onStart,
  onVerify,
  onComplete,
  onCancel,
}: TwoFactorSetupWizardProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<0 | 1>(0);
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [startError, setStartError] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoadingSetup(true);
    setStartError("");
    onStart()
      .then((data) => {
        if (active) setSetup(data);
      })
      .catch((err: any) => {
        if (active) {
          setStartError(
            err.response?.data?.message || t("app.twoFactor.setup.startFailed"),
          );
        }
      })
      .finally(() => {
        if (active) setLoadingSetup(false);
      });
    return () => {
      active = false;
    };
    // Run once on mount — the wizard is remounted per enrollment, so we intentionally
    // do not re-run when the injected callbacks change identity between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = async () => {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      notify({ type: "success", title: t("app.twoFactor.setup.secretCopied") });
    } catch {
      // Clipboard can be unavailable (insecure context); the secret is still visible to type.
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const trimmed = code.trim();
    if (trimmed.length !== 6) {
      setError(t("app.twoFactor.invalidCodeFormat"));
      return;
    }
    setVerifying(true);
    try {
      await onVerify(trimmed);
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.twoFactor.verifyFailed"));
    } finally {
      setVerifying(false);
    }
  };

  if (loadingSetup) {
    return (
      <Flex justify="center" align="center" py={10}>
        <Spinner />
      </Flex>
    );
  }

  if (startError) {
    return (
      <VStack gap={4} align="stretch">
        <Text fontSize="sm" color={getThemeColor("alert.error.text")}>
          {startError}
        </Text>
        {onCancel && (
          <Button intent="secondary" onClick={onCancel}>
            {t("app.common.close")}
          </Button>
        )}
      </VStack>
    );
  }

  return (
    <VStack gap={5} align="stretch">
      {step === 0 && setup && (
        <>
          <VStack gap={1} align="start">
            <Heading size="md">{t("app.twoFactor.setup.step1Title")}</Heading>
            <Text fontSize="sm" color={getThemeColor("text.secondary")}>
              {t("app.twoFactor.setup.step1Instruction")}
            </Text>
          </VStack>

          {/* QR code on a white card so it scans in dark mode */}
          <Flex justify="center">
            <Box bg="white" p={4} borderRadius="lg">
              <QRCodeSVG value={setup.otpauthUri} size={192} level="M" />
            </Box>
          </Flex>

          <Box>
            <Button
              intent="ghost"
              size="sm"
              onClick={() => setShowSecret((v) => !v)}
            >
              {showSecret
                ? t("app.twoFactor.setup.hideManualCode")
                : t("app.twoFactor.setup.revealManualCode")}
            </Button>
            {showSecret && (
              <Box
                mt={2}
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor={getThemeColor("border.default")}
              >
                <Text
                  fontSize="xs"
                  color={getThemeColor("text.secondary")}
                  mb={1}
                >
                  {t("app.twoFactor.setup.manualCodeHint")}
                </Text>
                <Flex justify="space-between" align="center" gap={2}>
                  <Text fontFamily="mono" fontSize="md" letterSpacing="wide">
                    {formatSecret(setup.secret)}
                  </Text>
                  <Button intent="secondary" size="sm" onClick={handleCopy}>
                    {t("app.common.copy")}
                  </Button>
                </Flex>
              </Box>
            )}
          </Box>

          <Flex justify="space-between" gap={3}>
            {onCancel ? (
              <Button intent="secondary" onClick={onCancel}>
                {t("app.common.cancel")}
              </Button>
            ) : (
              <Box />
            )}
            <Button intent="primary" onClick={() => setStep(1)}>
              {t("app.common.next")}
            </Button>
          </Flex>
        </>
      )}

      {step === 1 && (
        <form onSubmit={handleVerify}>
          <VStack gap={5} align="stretch">
            <VStack gap={1} align="start">
              <Heading size="md">{t("app.twoFactor.setup.step2Title")}</Heading>
              <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                {t("app.twoFactor.setup.step2Instruction")}
              </Text>
            </VStack>

            <Input
              id="twoFactorCode"
              label={t("app.twoFactor.codeLabel")}
              placeholder="000000"
              size="lg"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              autoFocus
            />

            {error && (
              <Text
                fontSize="sm"
                color={getThemeColor("alert.error.text")}
                fontWeight="medium"
              >
                {error}
              </Text>
            )}

            <Flex justify="space-between" gap={3}>
              <Button
                intent="secondary"
                type="button"
                onClick={() => setStep(0)}
              >
                {t("app.common.back")}
              </Button>
              <Button intent="primary" type="submit" disabled={verifying}>
                {verifying
                  ? t("app.twoFactor.verifying")
                  : t("app.twoFactor.setup.verifyAndEnable")}
              </Button>
            </Flex>
          </VStack>
        </form>
      )}
    </VStack>
  );
}
